/**
 * AuiChartBase — Shared renderer for all ECharts-based AUI components.
 *
 * Handles the option/optionUrl dual-mode, loading/error states, and
 * delegates to the base <Chart> wrapper. Each concrete chart component
 * (AuiBarChart, AuiLineChart, etc.) simply re-exports this with a
 * data-testid.
 */

import { Skeleton } from '@/components/ui/skeleton';
import { Chart } from '@/components/ui/chart';
import { useChartOption } from './useChartOption';
import type { AuiRendererProps } from '../../registry';

interface Props extends AuiRendererProps {
  testId: string;
}

export function AuiChartBase({ node, testId }: Props) {
  const { option, height, loading, error } = useChartOption(node);

  if (loading) {
    return (
      <div className="rounded-lg border border-border p-4" data-testid={testId}>
        <Skeleton className="w-full" style={{ height }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-border p-4" data-testid={testId}>
        <p className="text-sm text-destructive">
          Failed to load chart data: {error}
        </p>
      </div>
    );
  }

  if (!option) {
    return (
      <div className="rounded-lg border border-border p-4" data-testid={testId}>
        <p className="text-sm text-muted-foreground">No chart data</p>
      </div>
    );
  }

  return (
    <div data-testid={testId}>
      <Chart option={option} height={height} />
    </div>
  );
}
